import type { CallLeaveReason } from "pages/Meetings/call/types";
import type { CallParticipant, TransportEvent } from "pages/Meetings/call/custom/callTransport";

/** The hosted website negotiates capabilities; native code owns permissions and origin checks. */
export type NativeCapabilities = {
  bridgeVersion: number;
  platform: 'ios' | 'android';
  nativeCall: boolean;
  meetingTools?: boolean;
  screenShare?: boolean;
  localPreview?: boolean;
  videoQuality?: boolean;
  noiseSuppression?: boolean;
  backgroundEffects?: boolean;
  pictureInPicture: boolean;
  /** Optional v2 handshake: OS restore completes after the visible viewport is acknowledged. */
  pipRestoreAck?: boolean;
  /** Native peer retains local hand metadata through hosted-page reloads. */
  handStateRetention?: boolean;
  /** Exact-grant retirement, native ownership recovery, and scoped retry. */
  grantRetirement?: boolean;
  /** Atomic cancellation/adoption and authoritative media cleanup metadata. */
  grantRetirementFencing?: boolean;
  remotePush: boolean;
  waitingRoom?: boolean;
};

export type NativeCallState = {
  state: 'idle' | 'preview' | 'joining' | 'joined' | 'waiting' | 'denied' | 'left' | 'error';
  meetingId?: string;
  participantId?: string;
  accessId?: string;
  operationId?: string;
  roomUrl?: string;
  reason?: CallLeaveReason;
  message?: string;
  errorCode?: 'NATIVE_CALL_CONTRACT_REQUIRED';
  cleanupComplete?: boolean;
  cleanupPending?: boolean;
  accessVerifying?: boolean;
  handStateRetention?: boolean;
  /** User choices survive native teardown and verification pauses; never screen capture. */
  mediaChoices?: { audio: boolean; video: boolean };
  generation?: number;
  participants?: Record<string, CallParticipant>;
  activeSpeakerId?: string;
  recording?: boolean;
  transcribing?: boolean;
  networkQuality?: number;
  cameraFacing?: "user" | "environment";
  devices?: { deviceId: string; label: string; kind: "audiooutput" | "videoinput" | "audioinput" }[];
  audioRoute?: string;
  videoQuality?: { send: 'low' | 'medium' | 'high'; receive: 'low' | 'medium' | 'high' };
};

export type NativeCallRetirement = {
  operationId: string;
  accessId: string;
  meetingId: string;
  status: 'ready' | 'pending' | 'confirmed' | 'unknown';
  /** Includes a controller whose local cleanup is still pending or failed. */
  bound: boolean;
};

// Project only public metadata, even if a future native build adds internal fields.
const retirementRecord = (value: unknown): NativeCallRetirement => {
  const record = value as Partial<NativeCallRetirement> | null;
  if (!record || ![record.operationId, record.accessId, record.meetingId].every(field => typeof field === 'string' && field.length > 0 && field.length <= 128)
    || typeof record.bound !== 'boolean' || !['ready', 'pending', 'confirmed', 'unknown'].includes(record.status || '')) {
    throw new Error('Unable to read meeting access status. Please try again.');
  }
  return { operationId: record.operationId!, accessId: record.accessId!, meetingId: record.meetingId!, status: record.status!, bound: record.bound };
};

export function nativeCallFailureMessage(failure: unknown, fallback = 'Unable to join the meeting. Check your connection and device permissions, then try again.'): string {
  const code = (failure as { code?: string } | null)?.code;
  if (code === 'NATIVE_CALL_CONTRACT_REQUIRED') return 'Update the WiseFriend app and reload this page before joining a call.';
  if (code === 'CALL_GRANT_RETIRED') return 'Your previous meeting access is closing. Retry closing access below, then join again.';
  if (code === 'CALL_RETIREMENT_CAPACITY') return 'Unable to start another call yet. Use Close access or Retry closing access below, or wait for older access records to expire before joining.';
  if (code === 'CALL_ACTIVE') return 'That access belongs to an active call. Return to the call and use Leave.';
  if (code === 'UNKNOWN_RETIREMENT') return 'This access record is no longer available. Refresh its status and try joining again.';
  if (code === 'RETIREMENT_NOT_REQUESTED') return 'Leave this call before retrying access closure.';
  return fallback;
}

export type NativeListener = { remove: () => Promise<void> };
type NativeRuntime = {
  getPlatform?: () => string;
  isNativePlatform?: () => boolean;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
  nativeCallback?: (
    plugin: string, method: string, options: Record<string, unknown>,
    callback?: (data: unknown, error?: unknown) => void,
  ) => string;
};

const runtime = (): NativeRuntime | undefined => (
  typeof window === 'undefined' ? undefined : (window as Window & { Capacitor?: NativeRuntime }).Capacitor
);

export const isNativeMobile = (): boolean => {
  const cap = runtime();
  return Boolean(cap?.nativePromise && ['ios', 'android'].includes(cap.getPlatform?.() || ''));
};

export async function nativeInvoke<T>(plugin: string, method: string, options: Record<string, unknown> = {}): Promise<T> {
  const cap = runtime();
  if (!isNativeMobile() || !cap?.nativePromise) throw new Error('This feature is available in the WiseFriend app.');
  return cap.nativePromise(plugin, method, options) as Promise<T>;
}

export async function nativeListen<T>(plugin: string, eventName: string, listener: (event: T) => void): Promise<NativeListener> {
  const cap = runtime();
  if (!isNativeMobile() || !cap?.nativeCallback) throw new Error('Native events are unavailable.');
  let activeListener: ((event: T) => void) | undefined = listener;
  const callbackId = cap.nativeCallback(plugin, 'addListener', { eventName }, (data, error) => {
    if (!error) activeListener?.(data as T);
  });
  return { remove: async () => {
    if (!activeListener) return;
    activeListener = undefined;
    // Capacitor's removeListener is RETURN_NONE on Android: there is no native reply
    // to await. Dispatch without a response callback so cleanup cannot hang registration.
    cap.nativeCallback?.(plugin, 'removeListener', { eventName, callbackId });
  } };
}

export async function getNativeCapabilities(): Promise<NativeCapabilities | null> {
  if (!isNativeMobile()) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      nativeInvoke<NativeCapabilities>('WiseFriendMobile', 'getCapabilities'),
      new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), 4000); }),
    ]);
    return result && [1, 2].includes(result.bridgeVersion) && ['ios', 'android'].includes(result.platform) ? result : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export const nativeCall = {
  getState: () => nativeInvoke<NativeCallState>('WiseFriendMobile', 'getCallState'),
  getRetirements: async (options: { meetingId?: string } = {}): Promise<NativeCallRetirement[]> => {
    const result = await nativeInvoke<{ retirements: unknown[] }>('WiseFriendMobile', 'getCallRetirements', options);
    if (!Array.isArray(result?.retirements) || result.retirements.length > 1024) throw new Error('Unable to read meeting access status. Please try again.');
    return result.retirements.map(retirementRecord);
  },
  cancelUnadoptedAccess: async (options: { meetingId: string; accessId: string; lifecycleUrl: string; lifecycleToken: string }): Promise<NativeCallRetirement> => retirementRecord(
    await nativeInvoke<unknown>('WiseFriendMobile', 'cancelUnadoptedCallAccess', options),
  ),
  closeDormantAccess: async (operationId: string): Promise<NativeCallRetirement> => retirementRecord(
    await nativeInvoke<unknown>('WiseFriendMobile', 'closeDormantCallAccess', { operationId }),
  ),
  retryRetirement: async (operationId: string): Promise<NativeCallRetirement> => retirementRecord(
    await nativeInvoke<unknown>('WiseFriendMobile', 'retryCallRetirement', { operationId }),
  ),
  listenRetirements: (listener: (record: NativeCallRetirement) => void) => nativeListen<unknown>('WiseFriendMobile', 'callRetirementChanged', value => {
    let record: NativeCallRetirement;
    try { record = retirementRecord(value); } catch { return; }
    listener(record);
  }),
  show: () => nativeInvoke<void>('WiseFriendMobile', 'showCall'),
  leave: (scope?: Pick<NativeCallState, 'meetingId' | 'generation' | 'operationId'>) => nativeInvoke<void>('WiseFriendMobile', 'leaveCall', scope || {}),
  prepare: (options: { meetingId: string; displayName?: string; startAudioOff: boolean; startVideoOff: boolean }) => nativeInvoke<NativeCallState>('WiseFriendMobile', 'prepareCall', options),
  join: (options: { meetingId: string; accessId: string; url: string; token: string; displayName?: string; lifecycleUrl: string; lifecycleToken: string; startAudioOff?: boolean; startVideoOff?: boolean }) => (
    nativeInvoke<NativeCallState>('WiseFriendMobile', 'joinCall', options)
  ),
  command: <T = NativeCallState>(state: Pick<NativeCallState, 'meetingId' | 'generation' | 'accessVerifying'>, command: string, args: Record<string, unknown> = {}) => {
    if (state.accessVerifying) return Promise.reject(new Error('Meeting access is being verified. Call controls are temporarily paused.'));
    if (!state.meetingId || !Number.isInteger(state.generation)) return Promise.reject(new Error('The call is not ready.'));
    return nativeInvoke<T>('WiseFriendMobile', 'callCommand', { meetingId: state.meetingId, generation: state.generation, command, args });
  },
  viewport: (state: Pick<NativeCallState, 'meetingId' | 'generation'>, rect: { x: number; y: number; width: number; height: number; visible: boolean; viewMode: 'speaker' | 'grid'; pinnedId?: string }) => (
    nativeInvoke<void>('WiseFriendMobile', 'setVideoViewport', { meetingId: state.meetingId, generation: state.generation, ...rect })
  ),
  events: (listener: (event: TransportEvent & { meetingId?: string; generation?: number }) => void) => (
    nativeListen<TransportEvent & { meetingId?: string; generation?: number }>('WiseFriendMobile', 'callEvent', listener)
  ),
  listen: (listener: (state: NativeCallState) => void) => (
    nativeListen<NativeCallState>('WiseFriendMobile', 'callStateChanged', listener)
  ),
};

/** Only product destinations, never API endpoints, auth callbacks or arbitrary external URLs. */
const hasUnsafePathCharacters = (value: string) => Array.from(value).some((character) => character.charCodeAt(0) <= 32 || character === '\\');
export function mobileDestination(value: unknown, origin = window.location.origin): string | null {
  if (typeof value !== 'string' || value.length > 2048 || hasUnsafePathCharacters(value)) return null;
  try {
    let candidate = value;
    if (/^wisefriend(?:-poc|-staging)?:\/\//.test(candidate)) {
      const deep = new URL(candidate);
      candidate = `/${deep.hostname}${deep.pathname}${deep.search}${deep.hash}`;
    } else if (!candidate.startsWith('/') && !candidate.startsWith('https://')) {
      return null;
    }
    if (candidate.startsWith('//')) return null;
    const url = new URL(candidate, origin);
    if (url.origin !== origin || url.username || url.password) return null;
    const path = decodeURIComponent(url.pathname);
    if (hasUnsafePathCharacters(path) || path.includes('//')) return null;
    if (!/^\/(home|snapshot|mentorship|profile|my-training|courses|flows|meetings|challenges|program-surveys)(\/|$)/.test(path)) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** Provider room path only; credentials/query strings are never sent to moderation. */
export function nativeRoomName(roomUrl?: string): string | undefined {
  if (!roomUrl) return undefined;
  try {
    const url = new URL(roomUrl);
    if (url.protocol !== 'https:' || !url.hostname.endsWith('.daily.co') || url.username || url.password) return undefined;
    const parts = url.pathname.split('/').filter(Boolean);
    return parts.length === 1 && /^[A-Za-z0-9_-]+$/.test(parts[0]) ? parts[0] : undefined;
  } catch { return undefined; }
}
